import { appColors, BackrsChip, ContentNugget, NUGGET_THEMES } from '@backrs_ui/common-ui';

import { KeyboardArrowDown } from '@mui/icons-material';
import { Box, IconButton, Theme, SxProps } from '@mui/material';

import { determinePanelNuggetRender } from './nugget.util';

type DirectionType = 'row' | 'column' | 'scrollingRow';

type NuggetListProps = Readonly<{
  columnHeader?: string;
  nuggets: ContentNugget[];
  onClick: (nugget: ContentNugget) => void;
  completedNuggetIds?: number[];
  direction?: DirectionType;
  columnRef?: React.RefObject<HTMLDivElement>;
  handleScrollToNextColumn?: () => void;
}>;

export const NuggetList = ({
  columnHeader,
  nuggets,
  onClick,
  completedNuggetIds,
  direction = 'column',
  columnRef,
  handleScrollToNextColumn,
}: NuggetListProps) => {
  const isActivity = columnHeader === NUGGET_THEMES.GROW_BY_DOING;

  const renderedNuggets = nuggets.map((nugget: ContentNugget) => {
    const key = `${nugget.type}-${nugget.variant}-${nugget.id}`;

    return (
      <Box key={key} mt={2} sx={getNuggetStyles(direction)}>
        {determinePanelNuggetRender({
          nugget,
          completedNuggetIds,
          onClick,
          key: `${key}-nugget`,
        })}
      </Box>
    );
  });

  if (direction === 'column') {
    return (
      <Box sx={columnContentContainerStyle}>
        {columnHeader && (
          // with smaller screen sizes, we will sticky the header to the top so the user knows what column they are looking at
          <Box
            ref={columnRef}
            sx={{
              ...stickyHeaderStyle,
              backgroundColor: isActivity ? appColors.color.blue5 : appColors.color.taupe2,
            }}
          >
            <BackrsChip
              label={columnHeader}
              variant={{
                type: 'LARGE_TEXT',
                backgroundColor: 'BLUE',
              }}
            />
            {handleScrollToNextColumn && (
              <IconButton onClick={handleScrollToNextColumn} sx={stickyArrowButtonStyle}>
                <KeyboardArrowDown sx={stickyArrowStyle} />
              </IconButton>
            )}
          </Box>
        )}
        {renderedNuggets}
      </Box>
    );
  }

  if (direction === 'scrollingRow') {
    return <Box sx={horizontalScrollContainerStyle}>{renderedNuggets}</Box>;
  }

  return (
    <Box>
      {columnHeader && (
        <BackrsChip
          label={columnHeader}
          variant={{
            type: 'LARGE_TEXT',
            backgroundColor: 'BLUE',
          }}
        />
      )}
      {/* we need a column to display our rows of nuggets */}
      <Box sx={rowColumnContainerStyle}>
        {/* we will display three nuggets per row */}
        <Box sx={nuggetRowContainerStyle}>{renderedNuggets}</Box>
      </Box>
    </Box>
  );
};

const columnContentContainerStyle: SxProps<Theme> = {
  maxWidth: '70vw',
  minWidth: '300px',
  '@media (max-width: 937px)': {
    minWidth: 'unset',
    maxWidth: 'unset',
  },
};

const rowColumnContainerStyle: SxProps<Theme> = {
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  boxSizing: 'border-box',
  pb: 2,
};

const nuggetRowContainerStyle: SxProps<Theme> = {
  flexWrap: 'wrap',
  display: { xs: 'flex', sm: 'grid' },
  gridTemplateColumns: 'repeat(auto-fill, minmax(32%, 1fr))',
  gap: 2,
  height: '100%',
};

const renderedRowNuggetStyle: SxProps<Theme> = {
  width: {
    xs: '100%',
    sm: 'unset',
  },
};

const stickyHeaderStyle: SxProps<Theme> = {
  '@media (max-width: 837px)': {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    backgroundColor: appColors.color.taupe2,
    py: 2,
    justifyContent: 'space-between',
    display: 'flex',
    alignItems: 'center',
  },
};

const stickyArrowButtonStyle: SxProps<Theme> = {
  display: 'none',
  '@media (max-width: 837px)': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: appColors.neutral.white,
    borderRadius: '50%',
    border: `1px solid ${appColors.color.blue3}`,
    height: '32px',
    width: '32px',
  },
};

const stickyArrowStyle: SxProps<Theme> = {
  color: appColors.color.blue1,
  cursor: 'pointer',
};

const horizontalScrollContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  overflowX: 'auto',
  gap: backrs.spacing['spacing-3xl'],
});

const horizontalScrollNuggetStyle: SxProps<Theme> = {
  minWidth: '274px',
  maxWidth: '274px',
  flexShrink: 0,
};

const getNuggetStyles = (direction: DirectionType): SxProps<Theme> => {
  const styleMap: Record<DirectionType, SxProps<Theme>> = {
    row: renderedRowNuggetStyle,
    scrollingRow: horizontalScrollNuggetStyle,
    column: {},
  };

  return styleMap[direction];
};
